import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

// Each topic is one section of the page, reachable at /help#<id>. Add new
// features here; the table of contents is built from this list.
const TOPICS = [
    {
        id: 'presentation-mode',
        title: 'Presentation Mode',
        body: (
            <>
                <Typography paragraph>
                    Presentation Mode keeps your audience focused on your slides while you present.
                    Turn it on when you start presenting, and turn it off when you want everyone to
                    join in again.
                </Typography>

                <Typography variant='h6' component='h3'>Who can turn it on</Typography>
                <Typography paragraph>
                    Only room admins. The switch is in the bar across the top of the room, and also
                    under Settings in the room's side panel. While it is on, admins see the
                    switch and the border around the slide turn pink, so it is always clear that it
                    is live.
                </Typography>

                <Typography variant='h6' component='h3'>What changes for your audience</Typography>
                <Typography paragraph>
                    Under Settings, the list "Available during Presentation Mode" decides which
                    features your audience keeps while it is on. Anything switched off there is
                    unavailable until Presentation Mode is turned off:
                </Typography>
                <Box component='ul' sx={{ mt: 0 }}>
                    <li><Typography><strong>Drawing</strong> on the slide (off by default)</Typography></li>
                    <li><Typography><strong>Image Q</strong>, the panel of slide thumbnails where people can look through the slides and add images (off by default)</Typography></li>
                    <li><Typography><strong>Chat</strong> (on by default)</Typography></li>
                    <li><Typography><strong>Reactions</strong> on the slide (on by default)</Typography></li>
                </Box>
                <Typography paragraph>
                    You can change this list at any time. It only matters while Presentation Mode
                    is on. Everyone can always see the slide you are showing, and room admins
                    always keep every feature.
                </Typography>

                <Typography variant='h6' component='h3'>Members and guests</Typography>
                <Typography paragraph>
                    <strong>Members</strong> are people who signed in and joined the room. They get
                    the features in the list above, including anyone who joins while Presentation
                    Mode is already on.
                </Typography>
                <Typography paragraph>
                    <strong>Guests</strong> are watching through a shared link without signing in.
                    Guests can only watch: they never draw, chat or react. While Presentation Mode
                    is on, they also stop seeing the chat or the Image Q if you have switched those
                    off.
                </Typography>

                <Typography variant='h6' component='h3'>Giving one person full access</Typography>
                <Typography paragraph>
                    In the People list, admins see a hand icon next to each member who is online.
                    A coloured hand means that person has full access; a grey hand means the
                    Presentation Mode list applies to them. Click someone's hand to switch them
                    between the two, for example to let a co-presenter draw while everyone else
                    watches. The hand at the top of the Members list does the same for everyone at
                    once.
                </Typography>
                <Typography paragraph>
                    Turning Presentation Mode on or off resets everyone: turning it on limits every
                    member, and turning it off gives every member full access again. Any one-off
                    exceptions you made are cleared at that point.
                </Typography>
            </>
        )
    }
];

export default function Help() {
    // The page is usually opened in a new tab at /help#<topic>. The browser
    // looks for the anchor before React has rendered it, so scroll to it once
    // the sections exist.
    useEffect(() => {
        const id = window.location.hash.slice(1);
        const target = id && document.getElementById(id);
        if (target && target.scrollIntoView) {
            target.scrollIntoView();
        }
    }, []);

    return (
        <Container maxWidth='md' sx={{ py: 4 }}>
            <Typography variant='h4' component='h1' gutterBottom>
                VYU1 Help
            </Typography>
            <Typography color='text.secondary' paragraph>
                What the room settings do, in plain words.
            </Typography>
            <Box component='nav' aria-label='Help topics' sx={{ mb: 2 }}>
                <Box component='ul' sx={{ m: 0 }}>
                    {TOPICS.map((t) => (
                        <li key={t.id}>
                            <Link href={`#${t.id}`}>{t.title}</Link>
                        </li>
                    ))}
                </Box>
            </Box>
            {TOPICS.map((t) => (
                <Box component='section' key={t.id} id={t.id} aria-labelledby={`${t.id}-title`} sx={{ scrollMarginTop: 16 }}>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant='h5' component='h2' id={`${t.id}-title`} gutterBottom>
                        {t.title}
                    </Typography>
                    {t.body}
                </Box>
            ))}
        </Container>
    );
}
